import React from "react";
import { Box } from "@mui/material";
import {
  useTrackToggle,
  useMediaDevices,
  useDisconnectButton,
  useMediaDeviceSelect,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import {
  ScreenShareIcon,
  ScreenShareOffIcon,
  PhoneOffIcon,
} from "lucide-react";
import MicrophoneButton from "./MicrophoneButton";
import VideoButton from "./VideoButton";

const VideoControlBar: React.FC = () => {
  const { toggle: toggleMic, enabled: micEnabled } = useTrackToggle({
    source: Track.Source.Microphone,
  });
  const { toggle: toggleCamera, enabled: cameraEnabled } = useTrackToggle({
    source: Track.Source.Camera,
  });
  const { toggle: toggleScreenShare, enabled: screenShareEnabled } =
    useTrackToggle({
      source: Track.Source.ScreenShare,
    });

  const audioDevices = useMediaDevices({ kind: "audioinput" });
  const videoDevices = useMediaDevices({ kind: "videoinput" });

  const {
    activeDeviceId: activeAudioDeviceId,
    setActiveMediaDevice: setActiveAudioDevice,
  } = useMediaDeviceSelect({ kind: "audioinput" });
  const {
    activeDeviceId: activeVideoDeviceId,
    setActiveMediaDevice: setActiveVideoDevice,
  } = useMediaDeviceSelect({ kind: "videoinput" });

  const { buttonProps } = useDisconnectButton({});

  return (
    <Box
      className="video-control-bar"
      sx={{
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      {/* Microphone Button */}
      <MicrophoneButton
        enabled={micEnabled}
        onToggle={toggleMic}
        devices={audioDevices}
        activeDeviceId={activeAudioDeviceId}
        onDeviceChange={setActiveAudioDevice}
      />

      {/* Video Button */}
      <VideoButton
        enabled={cameraEnabled}
        onToggle={toggleCamera}
        devices={videoDevices}
        activeDeviceId={activeVideoDeviceId}
        onDeviceChange={setActiveVideoDevice}
      />

      {/* Screen Share */}
      <Box
        onClick={() => toggleScreenShare()}
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          backgroundColor: screenShareEnabled ? "primary.main" : "#636365",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          color: "white",
          "&:hover": {
            backgroundColor: screenShareEnabled
              ? "primary.dark"
              : "rgba(255, 255, 255, 0.2)",
            transform: "scale(1.05)",
          },
          transition: "all 0.2s ease",
        }}
      >
        {screenShareEnabled ? (
          <ScreenShareOffIcon size={20} />
        ) : (
          <ScreenShareIcon size={20} />
        )}
      </Box>

      {/* Leave Button */}
      <Box
        component="button"
        {...buttonProps}
        sx={{
          backgroundColor: "secondary.dark",
          color: "white",
          border: "none",
          borderRadius: "50%",
          width: "40px",
          height: "40px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PhoneOffIcon size={20} />
      </Box>
    </Box>
  );
};

export default VideoControlBar;
